import type { Locale } from './locales';

/**
 * Locale-aware number, date and duration formatting.
 *
 * <p><strong>A Slovenian reader seeing `87.3 km/h` reads eighty-seven thousand three
 * hundred.</strong> The decimal comma is not a preference in `sl` and `hr`, it is the
 * separator, and the dot is the thousands grouping — so an untranslated number is not
 * merely foreign-looking, it is a different number. This is the single highest-risk
 * formatting difference in the product, because speed and distance appear on every screen
 * and neither has a unit that makes the mistake obvious.
 *
 * <p>Every formatter is built through the cache below rather than inline.
 * `Intl.NumberFormat` construction is expensive enough to matter in a list that
 * re-renders every fifteen seconds, and the instances are immutable, so there is no
 * reason to build one per row.
 */
const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>();

function cached<T extends Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>(
  key: string,
  build: () => T,
): T {
  const hit = cache.get(key);
  if (hit) return hit as T;
  const made = build();
  cache.set(key, made);
  return made;
}

export interface Formatters {
  /** A plain count: fleet sizes, fix counts, satellites. */
  number: (value: number) => string;
  /** Fixed decimals, for speeds and distances. */
  decimal: (value: number, digits?: number) => string;
  /** 0–1 as a percentage, or pass `whole: true` for a value already in percent. */
  percent: (value: number) => string;
  /** Kilometres from metres, at one decimal. */
  kilometres: (metres: number) => string;
  /**
   * Money from integer minor units (4900 -> €49.00), in the given ISO 4217 currency.
   *
   * <p>Minor units in, never a float: 0.1 + 0.2 is not a price. The division happens here,
   * once, at the point the value stops being arithmetic and becomes text.
   */
  money: (minorUnits: number, currency: string) => string;
  /** Date and time, medium length: what a log line correlates against. */
  dateTime: (value: Date | string | number) => string;
  /** Date only. */
  date: (value: Date | string | number) => string;
  /** Time only, to the minute. */
  time: (value: Date | string | number) => string;
  /**
   * "14 minutes ago" / "pred 14 minutami" — the most repeated string on the fleet map.
   *
   * <p>Takes a signed millisecond delta: negative is the past. Picks its own unit, so a
   * caller never has to decide between minutes and hours.
   */
  relative: (deltaMs: number) => string;
}

const UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: 'year', ms: 31_536_000_000 },
  { unit: 'month', ms: 2_592_000_000 },
  { unit: 'day', ms: 86_400_000 },
  { unit: 'hour', ms: 3_600_000 },
  { unit: 'minute', ms: 60_000 },
  { unit: 'second', ms: 1_000 },
];

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

export function formattersFor(locale: Locale): Formatters {
  const number = cached(`n:${locale}`, () => new Intl.NumberFormat(locale));
  const dateTime = cached(
    `dt:${locale}`,
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'medium' }),
  );
  const dateOnly = cached(
    `d:${locale}`,
    () => new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }),
  );
  const timeOnly = cached(
    `t:${locale}`,
    () => new Intl.DateTimeFormat(locale, { timeStyle: 'short' }),
  );
  // `numeric: 'auto'` is what turns "1 day ago" into "yesterday", in every language that
  // has a word for it -- which is the phrasing a person would actually use.
  const relative = cached(
    `r:${locale}`,
    () => new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }),
  );

  return {
    number: (value) => number.format(value),

    decimal: (value, digits = 1) =>
      cached(
        `n${digits}:${locale}`,
        () =>
          new Intl.NumberFormat(locale, {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
          }),
      ).format(value),

    percent: (value) =>
      cached(
        `p:${locale}`,
        () => new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }),
      ).format(value / 100),

    kilometres: (metres) =>
      cached(
        `n1:${locale}`,
        () => new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
      ).format(metres / 1000),

    money: (minorUnits, currency) => {
      try {
        return cached(
          `m:${currency}:${locale}`,
          () => new Intl.NumberFormat(locale, { style: 'currency', currency }),
        ).format(minorUnits / 100);
      } catch {
        // An unknown currency code throws a RangeError; show the amount rather than nothing.
        return `${number.format(minorUnits / 100)} ${currency}`;
      }
    },

    dateTime: (value) => dateTime.format(toDate(value)),
    date: (value) => dateOnly.format(toDate(value)),
    time: (value) => timeOnly.format(toDate(value)),

    relative: (deltaMs) => {
      const absolute = Math.abs(deltaMs);
      // Below the smallest unit `Intl` would round to zero and render "in 0 seconds",
      // which reads as broken. The caller's "just now" wording belongs in the catalogue,
      // so this is the one case the formatter refuses to answer.
      const match = UNITS.find((candidate) => absolute >= candidate.ms) ?? UNITS[UNITS.length - 1]!;
      return relative.format(Math.round(deltaMs / match.ms), match.unit);
    },
  };
}
